/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { isLightColor } from "../../utils/color_helpers";
import { 
  Box,
  Chip, 
  Divider,
  IconButton, 
  Popover, 
  Typography 
} from "@mui/material";

const CHIP_TEXT_COLOR = 'rgba(0, 0, 0, 0.87)';
const POPULAR_CARD_TEXT_COLOR = 'white';
const DEFAULT_CARD_HEIGHT = '23px';
const RESTAURANT_ASSIGNMENT_WIDTH = '380px';
const OVERFLOW_INDICATOR_WIDTH = 40;

const getReadableTextColor = (color) => (
  isLightColor(color?.toLowerCase()) ? CHIP_TEXT_COLOR : POPULAR_CARD_TEXT_COLOR
);

export const RenderDiscountCardsList = React.memo(({
  data,
  showRestaurantNames = false,
  maxHeight = '220px',
  showCountOnHover = false,
  countValue,
  popoverTitle,
  popoverSubtitle,
  maxWidth,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [visibleCards, setVisibleCards] = useState([]);
  const [hiddenCards, setHiddenCards] = useState([]);
  const containerRef = useRef(null);
  const measurementRefs = useRef(new Map());
  const hoverTimeout = useRef(null);

  const getItemKey = useCallback((item, index) => (
    item?.id ?? `${item?.name || 'card'}-${index}`
  ), []);

  const getMeasuredWidth = useCallback((item, index) => {
    const element = measurementRefs.current.get(getItemKey(item, index));

    if (!element) return 0;

    return Math.ceil(element.getBoundingClientRect().width) + 3;
  }, [getItemKey]);

  const getCardChipSx = useCallback((item, sx = {}) => ({
    fontWeight: 550,
    height: DEFAULT_CARD_HEIGHT,
    backgroundColor: item?.color || sx?.backgroundColor,
    color: getReadableTextColor(item?.color || sx?.backgroundColor),
    flexShrink: 0,
    minWidth: 'auto',
    marginRight: '3px',
    '& .MuiChip-label': {
      whiteSpace: 'nowrap',
    },
    ...sx,
  }), []);

  const calculateVisibleCards = useCallback(() => {
    if (showCountOnHover) return;
    if (!containerRef.current || !data?.length) return;

    const container = containerRef.current;
    const containerWidth = container.clientWidth;

    if (!containerWidth) return;

    let usedWidth = 0;
    let visibleCount = 0;

    for (let index = 0; index < data.length; index += 1) {
      const cardWidth = getMeasuredWidth(data[index], index);

      if (!cardWidth) return;

      const remainingCards = data.length - (index + 1);
      const reservedOverflowWidth = remainingCards > 0 ? OVERFLOW_INDICATOR_WIDTH : 0;

      if (usedWidth + cardWidth + reservedOverflowWidth <= containerWidth) {
        usedWidth += cardWidth;
        visibleCount += 1;
        continue;
      }

      break;
    }

    setVisibleCards(data.slice(0, visibleCount));
    setHiddenCards(data.slice(visibleCount));
  }, [data, getMeasuredWidth, showCountOnHover]);

  useEffect(() => {
    if (showCountOnHover) return;
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      calculateVisibleCards();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [calculateVisibleCards, showCountOnHover]);

  useEffect(() => {
    calculateVisibleCards();
  }, [data, calculateVisibleCards]);

  const handleOpenPopover = (event) => {
    if (hiddenCards?.length > 0 || showCountOnHover) {
      clearTimeout(hoverTimeout.current);
      setAnchorEl(event.currentTarget);
      setIsPopoverOpen(true);
    }
  };

  const handleClosePopover = () => {
    hoverTimeout.current = setTimeout(() => {
      setAnchorEl(null);
      setIsPopoverOpen(false);
    }, 150);
  };

  const handlePopoverMouseEnter = () => {
    clearTimeout(hoverTimeout.current);
    setIsPopoverOpen(true);
  };

  const handlePopoverMouseLeave = () => {
    handleClosePopover();
  };

  const open = Boolean(anchorEl) && isPopoverOpen;
  const cardsForPopover = showCountOnHover ? data : hiddenCards;

  const renderCardChip = (item, sx = {}) => (
    <Chip
      key={item?.id || item?.name || sx?.key}
      label={item?.name}
      size="small"
      sx={getCardChipSx(item, sx)}
    />
  );

  const renderPopover = (children, paperSx = {}) => (
    <Popover
      id="mouse-over-popover"
      open={open}
      anchorEl={anchorEl}
      onClose={handleClosePopover}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: {
            minWidth: paperSx.minWidth || (hiddenCards?.length > 0 ? '200px' : '150px'),
            borderRadius: paperSx.borderRadius || '8px',
            overflow: 'hidden',
            marginTop: '5px',
            ...paperSx,
          },
          onMouseEnter: handlePopoverMouseEnter,
          onMouseLeave: handlePopoverMouseLeave
        }
      }}
      disableRestoreFocus
    >
      {children}
    </Popover>
  );

  const renderPopoverCards = (items, chipSx = {}) => (
    <Box sx={{ padding: '16px' }}>
      {items?.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {items.map((item, index) => renderCardChip(item, { ...chipSx, key: index }))}
        </Box>
      )}
    </Box>
  );

  const renderChip = (label) => (
    <Chip
      sx={{
        backgroundColor: "white",
        "&:hover": {
          backgroundColor: "white",
        },
        height: "23px",
        border: "2px solid lightgray",
      }}
      label={label}
    />
  );

  const renderRestaurantAssignments = () => (
    <Box sx={{ width: '100%', maxWidth: RESTAURANT_ASSIGNMENT_WIDTH, minWidth: '360px', boxSizing: 'border-box' }}>
      {(popoverTitle || popoverSubtitle) && (
        <Box sx={{ px: 2, py: 1.5 }}>
          {popoverTitle && (
            <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#1f2937' }}>
              {popoverTitle}
            </Typography>
          )}
          {popoverSubtitle && (
            <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
              {popoverSubtitle}
            </Typography>
          )}
        </Box>
      )}

      <Divider />

      <Box
        sx={{
          maxHeight,
          overflowY: 'auto',
          width: '100%',
          maxWidth: RESTAURANT_ASSIGNMENT_WIDTH,
        }}
      >
        {data?.map((item, index) => (
          <Box key={`${item?.name}-${index}`}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '150px minmax(0, 1fr)',
                alignItems: 'center',
                columnGap: 2,
                px: 2,
                py: 1.25,
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <Chip
                label={item?.name}
                size="small"
                sx={{
                  backgroundColor: item?.color || '#9e9e9e',
                  color: getReadableTextColor(item?.color),
                  fontWeight: 550,
                  height: '28px',
                  minWidth: '64px',
                  maxWidth: '145px',
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    px: 1.5,
                  },
                }}
              />

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.75,
                  maxHeight: '72px',
                  overflowY: 'auto',
                  pr: 0.25,
                  minWidth: 0,
                }}
              >
                {(item?.restaurant_names || []).map((restaurantName, restIndex) => (
                  <Chip
                    key={`${item?.name}-${restaurantName}-${restIndex}`}
                    label={restaurantName}
                    size="small"
                    sx={{
                      backgroundColor: '#edf2f7',
                      color: '#64748b',
                      fontWeight: 500,
                      height: '28px',
                      maxWidth: '160px',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
            {index < data?.length - 1 && <Divider />}
          </Box>
        ))}
      </Box>
    </Box>
  );

  if (data?.length === 0) {
    return renderChip("No cards");
  }

  if (showCountOnHover) {
    const val = countValue ?? data?.length ?? 0;
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center" }}>
        <Typography
          onMouseEnter={handleOpenPopover}
          sx={{ cursor: "pointer", fontSize: "14px" }}
        >
          {renderChip(
            `${val} ${val === 1 ? "Card" : "Cards"}`,
          )}
        </Typography>

        {renderPopover(
          showRestaurantNames
            ? renderRestaurantAssignments()
            : renderPopoverCards(cardsForPopover),
          {
            minWidth: showRestaurantNames
              ? RESTAURANT_ASSIGNMENT_WIDTH
              : cardsForPopover?.length > 0
                ? "200px"
                : "150px",
            borderRadius: showRestaurantNames ? "12px" : "8px",
          },
        )}
      </Box>
    );
  }

  if (showRestaurantNames) {
    return renderRestaurantAssignments();
  }

  return (
    <Box
      ref={containerRef}
      data-testid="discount-cards-list-container"
      sx={{
        display: 'flex',
        alignItems: 'center',
        minWidth: '50px',
        width: '100%',
        maxWidth: maxWidth || 'none',
        overflow: 'hidden',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          height: 0,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        {data?.map((item, index) => (
          <Chip
            key={`measurement-${getItemKey(item, index)}`}
            ref={(element) => {
              if (element) {
                measurementRefs.current.set(getItemKey(item, index), element);
              } else {
                measurementRefs.current.delete(getItemKey(item, index));
              }
            }}
            label={item?.name}
            size="small"
            sx={getCardChipSx(item)}
          />
        ))}
      </Box>

      {visibleCards?.map((item, index) => (
        <Chip
          key={getItemKey(item, index)}
          sx={getCardChipSx(item, {
            '&:hover': {
              backgroundColor: item?.color,
              color: getReadableTextColor(item?.color),
            },
          })}
          label={item?.name}
          onMouseEnter={handleOpenPopover}
        />
      ))}

      {hiddenCards.length > 0 && (
        <IconButton
          data-testid="discount-cards-overflow-indicator"
          onMouseEnter={handleOpenPopover}
          sx={{
            height: '25px',
            width: '25px',
            backgroundColor: 'lightgray',
            "&:hover": {
              backgroundColor: 'lightgray'
            },
            flexShrink: 0,
            minWidth: '25px'
          }}
        >
          <Typography sx={{ fontSize: '14px' }}>
            {`+${hiddenCards.length}`}
          </Typography>
        </IconButton>
      )}

      {renderPopover(renderPopoverCards(cardsForPopover), {
        minWidth: hiddenCards?.length > 0 ? '200px' : '150px',
      })}
    </Box>
  );
});

RenderDiscountCardsList.displayName = 'RenderDiscountCardsList'
